/**
 * Utilities for working with const-object enums emitted by
 * `packages/types/scripts/generate-from-db.mjs` (and hand-written enums that
 * follow the same shape).
 *
 * These helpers exist so call sites don't have to sprinkle
 * `Object.values(X) as readonly string[]` casts or re-allocate the values
 * array on every iteration. They are intentionally tiny and zero-dependency
 * — see the "const-object enum" pattern documented in `AGENTS.md`.
 *
 * ### Usage
 *
 * ```ts
 * import { BankTransactionDirection, enumValues, isEnumValue } from '@alinea/types';
 * 
 *
 * // Iterate (cached — allocates once per enum, not once per call):
 * const all = enumValues(BankTransactionDirection); // readonly ['DEBIT', 'CREDIT']
 *
 * // Narrow an untyped string to the enum union in one step:
 * if (isEnumValue(BankTransactionDirection, userInput)) {
 *   // userInput is now typed as BankTransactionDirection
 * }
 * ```
 *
 * ### Non-goals
 *
 * - Do NOT use these helpers inside the enum object itself (e.g. packing
 *   `.values` / `.has` onto the const object): that breaks `z.nativeEnum(X)`
 *   because zod would treat those own-properties as valid enum members.
 * - Do NOT replace `@IsEnum(X)` / `z.nativeEnum(X)` / `@ApiProperty({ enum: X })`
 *   — those already accept the bare const object and are the canonical form.
 *   Only use these helpers where you need a `readonly T[keyof T][]` tuple
 *   (e.g. `.includes()` guards, `@IsIn(...)`, JSON-Schema `enum` arrays).
 */

type StringEnum = Record<string, string>;

// One-time value array per enum, keyed by the enum object identity. Safe
// because const-object enums are module-scoped singletons.
const valuesCache = new WeakMap<StringEnum, readonly string[]>();

/**
 * Return the values of a const-object enum as a memoized, readonly,
 * narrowly-typed tuple.
 *
 * Equivalent runtime behavior to `Object.values(enumObject)` with two
 * improvements:
 *   1. The returned array is cached per-enum (one allocation total).
 *   2. The element type is `T[keyof T]` (the enum's union) instead of
 *      `T[keyof T][]` widened to `string[]`, so downstream `.includes()`
 *      narrows correctly without manual casts.
 */
export function enumValues<T extends StringEnum>(enumObject: T): readonly T[keyof T][] {
  const cached = valuesCache.get(enumObject);
  if (cached) {
    return cached as readonly T[keyof T][];
  }
  const values = Object.freeze(Object.values(enumObject)) as readonly T[keyof T][];
  valuesCache.set(enumObject, values);
  return values;
}

/**
 * Type guard: returns true if `value` is one of the enum's values, and
 * narrows `value` to the enum's union type for the caller.
 *
 * Prefer this over `(Object.values(X) as readonly string[]).includes(v)`.
 */
export function isEnumValue<T extends StringEnum>(
  enumObject: T,
  value: unknown,
): value is T[keyof T] {
  if (typeof value !== 'string') return false;
  return (enumValues(enumObject) as readonly string[]).includes(value);
}
